import { useState } from "react"
import { createPortal } from "react-dom"
import dynamic from "next/dynamic"
import { CarouselProvider, Slider, Slide, ButtonBack, ButtonNext } from "pure-react-carousel"
import Link from "next/link"
import cn from "classnames"
import richText from "utilities/richText"
import useWindowSize from "utilities/useWindowSize"
import useHasIntersected from "utilities/useHasIntersected"
import Image from "next/image"
import usePortalModal from "utilities/usePortalModal"
import Expand from "public/assets/expand.svg"
import Arrow from "public/assets/chevron-teal.svg"
import GalleryModal from "components/GalleryModal"

export default function GalleryAndContent({ blok }) {
  const { ref: animationRef, isIntersecting } = useHasIntersected()

  const width = useWindowSize().width
  const [selectedImg, setSelectedImg] = useState(0)

  const { ref, mounted, modalOpen, closeModal, focusRef } = usePortalModal([blok._uid], () => blok.images)

  const buttonStyles = "mx-0 pt-0 absolute top-1/2 -translate-y-1/2"

  const moveRight = () => (selectedImg < blok.images.length - 1 ? setSelectedImg(selectedImg + 1) : setSelectedImg(0))

  const moveLeft = () => (selectedImg !== 0 ? setSelectedImg(selectedImg - 1) : setSelectedImg(blok.images.length - 1))

  const container = cn("image-content-container flex flex-col gap-4 lg:gap-0 py-12 mx-auto max-w-screen-xl", {
    "lg:flex-row": blok.orientation === "content_first",
    "lg:flex-row-reverse": blok.orientation === "image_first",
  })

  const content = cn(
    "image-content relative z-50 px-6 sm:px-0 basis-1/2 self-center z-10 prose prose-h2:pb-4 prose-p:py-2",
    {
      "lg:pr-12": blok.orientation === "content_first",
      "lg:pl-12": blok.orientation === "image_first",
    },
  )

  return (
    <section
      className={cn("sm:px-6", blok.background_color && `bg-${blok.background_color}`)}
      id={blok?.id ? blok?.id : null}
    >
      <div className={container}>
        <div className={content}>{richText(blok.content)}</div>
        <div className="image-decor relative z-10 basis-1/2 lg:max-h-[500px] lg:min-h-96">
          <Link href={`#${blok._uid}`}>
            <div
              className={cn(
                "relative mx-auto mb-6 h-[280px] w-full max-w-2xl overflow-hidden rounded-10 sm:h-[380px] lg:w-[590px]",
              )}
            >
              <Image
                src={blok.images[selectedImg].filename}
                alt={blok.images[selectedImg].alt}
                placeholder={blok.images[selectedImg]?.blurDataURL ? "blur" : "empty"}
                blurDataURL={blok.images[selectedImg]?.blurDataURL}
                fill
                style={{ objectFit: "cover" }}
              />
              <Expand
                className={cn("absolute bottom-2", {
                  "right-2": blok.orientation === "content_first" && width > 1024,
                  "left-2": blok.orientation === "image_first" || width < 1024,
                })}
              />
            </div>
          </Link>
          <CarouselProvider
            isIntrinsicHeight
            totalSlides={blok.images.length}
            visibleSlides={width > 600 ? 4 : 3}
            infinite
            currentSlide={selectedImg}
            className={cn("relative max-w-2xl sm:mx-auto")}
          >
            <Slider className="mx-auto w-3/4 max-w-[500px] overflow-hidden md:w-full">
              {blok.images.map((photo, i) => (
                <Slide key={i} index={i} className={cn("px-1 lg:px-2")}>
                  <button onClick={() => setSelectedImg(blok.images.indexOf(photo))} className="h-full w-full max-w-xs">
                    <Image
                      placeholder={photo.image?.blurDataURL ? "blur" : "empty"}
                      blurDataURL={photo.image?.blurDataURL}
                      src={photo.filename}
                      alt={photo.alt}
                      width={200}
                      height={175}
                      className={cn({ "border-[3px] border-green": i === selectedImg })}
                      style={{ objectFit: "cover", layout: "intrinsic" }}
                    />
                  </button>
                </Slide>
              ))}
            </Slider>
            <div>
              <ButtonBack className={cn(buttonStyles, "left-2 lg:left-0")} onClick={moveLeft}>
                <Arrow className="rotate-90" />
              </ButtonBack>
              <ButtonNext className={cn(buttonStyles, "right-2 lg:right-0")} onClick={moveRight}>
                <Arrow className="-rotate-90" />
              </ButtonNext>
            </div>
          </CarouselProvider>
          {mounted && modalOpen
            ? createPortal(
                <GalleryModal images={blok.images} closeModal={closeModal} focusRef={focusRef} />,
                ref.current,
              )
            : null}
        </div>
      </div>
    </section>
  )
}
